// Home — the front door to the archive (Aurora · Apple-clean redesign).
//
// Design principle: Home is a *front door*, not a dashboard. The most
// recently-active workspace becomes a single confident hero with ONE
// loud action (Browse). Its identity + counts render exactly once — at
// one workspace the hero IS the archive (no duplicate card); at many, a
// grid appears with the hero's workspace structurally SUBTRACTED so it
// can never re-duplicate. Everything operational (Drive, upload queue,
// the over-cap cache meter, overall health) collapses into one truthful
// System chip in the header that opens a System drawer — so the calm
// surface carries zero ops pixels.
//
// Role-aware: a superadmin gets the System chip/drawer, per-workspace
// owner attribution, and the "Workspaces & people" management door; a
// regular user gets the same hero/grid/shelf minus those, plus a "My
// workspaces" door to their own /status. UI gating only — the server
// re-checks every privileged call.

import {
  ArrowRight,
  Plus,
  Activity,
  Users,
  ChevronDown,
  X,
  Search,
  Check,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";

import { SearchBar } from "./SearchBar";
import { SearchResults } from "./SearchResults";
import { useSearch } from "../lib/useSearch";
import { useAppState } from "../state/AppContext";

import { useAuth, decodeJwtClaims } from "../state/AuthContext";
import { formatRelative } from "../lib/timeFmt";
import { useSystemStatus } from "../state/SystemStatusContext";
import { AddWorkspaceModal } from "./AddWorkspaceModal";
import { ConnectClaudeModal } from "./ConnectClaudeModal";
import { SignOutButton } from "./SignOutButton";
import {
  workspaceHealthColor,
  workspaceHealthLabel,
  workspaceHealthReason,
  type HealthColor,
} from "../lib/workspaceHealth";
import type { SystemStatusResponse, SystemStatusWorkspace } from "../lib/api";

// ── small visual helpers ────────────────────────────────────────────

// Stable gradient per name (the Aurora avatar bubble). Inline style so
// it doesn't depend on Tailwind's arbitrary-value JIT for dynamic stops.
const AVATAR_GRADIENTS: Array<[string, string]> = [
  ["#8b6cff", "#6366f1"],
  ["#22c1c3", "#2d9cdb"],
  ["#fb923c", "#f43f5e"],
  ["#f472b6", "#8b6cff"],
  ["#34d399", "#0ea5a4"],
  ["#a78bfa", "#6d28d9"],
];
function gradientFor(seed: string): { background: string } {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const [a, b] = AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length]!;
  return { background: `linear-gradient(135deg, ${a}, ${b})` };
}

function Avatar({ name, size = 40 }: { name: string; size?: number }) {
  return (
    <div
      aria-hidden
      style={{ ...gradientFor(name), width: size, height: size, borderRadius: size / 3.2 }}
      className="flex-shrink-0 flex items-center justify-center font-semibold text-white shadow-sm"
    >
      <span style={{ fontSize: size * 0.4 }}>{(name[0] ?? "?").toUpperCase()}</span>
    </div>
  );
}

const HEALTH_DOT: Record<HealthColor, string> = {
  green: "bg-theme-success-text",
  yellow: "bg-theme-warning-text",
  red: "bg-theme-error-border",
  gray: "bg-theme-text-tertiary",
};
// NOTE: there is deliberately no colour→label map here. "Yellow" means
// three different things (stale poll / backfilling / retrying uploads)
// and collapsing them to "Syncing" told operators a DEAD sync was
// healthy for 17h. Use workspaceHealthLabel(), which reads the fields.
const HEALTH_TEXT: Record<HealthColor, string> = {
  green: "text-theme-success-text",
  yellow: "text-theme-warning-text",
  red: "text-theme-error-border",
  gray: "text-theme-text-tertiary",
};

// One accent gradient, reserved for the single primary CTA + the logo.
const ACCENT = "linear-gradient(135deg, #8b6cff, #6366f1)";

// Glass surface, two elevations. The hero floats highest; the grid +
// shelf sit one step lower, so hierarchy reads by depth, not by colour.
const GLASS = "bg-white/[0.045] backdrop-blur-[20px] backdrop-saturate-[1.6] border border-white/[0.10]";
const GLASS_QUIET = "bg-white/[0.03] backdrop-blur-[20px] backdrop-saturate-[1.4] border border-white/[0.08]";

// ── overall system health (the truthful chip) ──────────────────────
//
// Replaces the old hard-coded "All systems healthy" pill, which would
// happily claim green while the cache sat over cap. Derived from the
// worst per-workspace health, plus a dead upload queue and an over-cap
// local cache — the same signals the System drawer itemises.

type Health = { color: HealthColor; label: string };
const HEALTH_RANK: Record<HealthColor, number> = { green: 0, gray: 1, yellow: 2, red: 3 };

function deriveHealth(
  workspaces: SystemStatusWorkspace[],
  status: SystemStatusResponse | null,
): Health {
  const driveQueue = status?.drive_queue ?? null;
  let worst: HealthColor = workspaces.length === 0 ? "gray" : "green";
  for (const w of workspaces) {
    const c = workspaceHealthColor({ workspace: w, driveQueue });
    if (HEALTH_RANK[c] > HEALTH_RANK[worst]) worst = c;
  }
  const dead = driveQueue?.dead ?? 0;
  const over =
    status?.cache_bytes != null &&
    status?.cache_cap_bytes != null &&
    status.cache_cap_bytes > 0 &&
    status.cache_bytes > status.cache_cap_bytes;
  if ((dead > 0 || over) && HEALTH_RANK[worst] < HEALTH_RANK.yellow) worst = "yellow";

  const label =
    worst === "green"
      ? "All systems healthy"
      : worst === "gray"
        ? "No workspaces"
        : "Needs attention";
  return { color: worst, label };
}

// ── page ────────────────────────────────────────────────────────────

function greetingWord(h: number): string {
  if (h < 5) return "Good night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function Chooser() {
  const { role, token } = useAuth();
  const isSuperadmin = role === "superadmin";
  const username = (decodeJwtClaims(token)?.sub as string | undefined) || "there";
  const [addOpen, setAddOpen] = useState(false);
  const [claudeOpen, setClaudeOpen] = useState(false);
  const [systemOpen, setSystemOpen] = useState(false);

  // Real cross-archive search ON the Home page (no redirect): the same
  // SearchBar/SearchResults/useSearch trio Browse uses, pinned to the
  // "all workspaces" scope (there is no active workspace here). A hit
  // navigates straight to the message permalink.
  const navigate = useNavigate();
  const { setOpenThreadParent } = useAppState();
  const search = useSearch({ activeWorkspaceId: null, activeConversationId: null, forceScope: "all" });
  const [resultsOpen, setResultsOpen] = useState(false);

  const { status } = useSystemStatus();
  const workspaces = useMemo(() => status?.workspaces ?? [], [status]);
  const driveQueue = status?.drive_queue ?? null;
  const health = useMemo(() => deriveHealth(workspaces, status), [workspaces, status]);

  const summary = useMemo(() => {
    if (!status) return null;
    return {
      workspaces: workspaces.length,
      messages: workspaces.reduce((a, w) => a + (w.message_count || 0), 0),
      attachments: workspaces.reduce((a, w) => a + (w.attachment_count || 0), 0),
    };
  }, [status, workspaces]);

  // The hero = the workspace with the most recent activity.
  const hero = useMemo(() => {
    if (workspaces.length === 0) return null;
    return [...workspaces].sort(
      (a, b) =>
        (b.last_message_at ? Date.parse(b.last_message_at) : 0) -
          (a.last_message_at ? Date.parse(a.last_message_at) : 0) ||
        // Stable tie-break so equal timestamps pick deterministically.
        a.team_id.localeCompare(b.team_id),
    )[0]!;
  }, [workspaces]);

  // The grid shows every workspace EXCEPT the hero's — subtracted so the
  // featured archive can never render twice. Appears only at N ≥ 2.
  const others = useMemo(
    () => workspaces.filter((w) => w.team_id !== hero?.team_id),
    [workspaces, hero],
  );

  // How many workspaces are not healthy — the number an admin manages by.
  const attentionCount = useMemo(
    () =>
      workspaces.filter((w) => {
        const c = workspaceHealthColor({ workspace: w, driveQueue });
        return c === "red" || c === "yellow";
      }).length,
    [workspaces, driveQueue],
  );

  // A superadmin manages the whole estate — potentially hundreds of
  // workspaces — so they get a dense, searchable console instead of a
  // wall of cards. Regular users (their own few) keep the friendly view.
  const useConsole = isSuperadmin;

  return (
    <div data-testid="chooser" className="min-h-screen flex flex-col text-theme-text-primary">
      {/* ── header ─────────────────────────────────────────────── */}
      <header className="flex items-center gap-4 px-6 sm:px-10 py-6 flex-shrink-0">
        <Link to="/" className="flex items-center gap-3 flex-shrink-0" aria-label="Home">
          <div
            aria-hidden
            style={{ background: ACCENT }}
            className="w-8 h-8 rounded-[10px] flex items-center justify-center text-white shadow-[0_3px_12px_rgba(124,92,255,0.45)]"
          >
            <span className="text-base font-bold">✦</span>
          </div>
          <span className="hidden sm:inline text-h3 font-semibold tracking-tightish">Slack Archive</span>
        </Link>

        <div data-testid="chooser-search" className="hidden md:block relative flex-1 max-w-lg mx-auto">
          <SearchBar
            query={search.query}
            onQueryChange={search.setQuery}
            scope={search.scope}
            onScopeChange={search.setScope}
            conversationScopeAvailable={false}
            hideScope
            onOpenResults={() => setResultsOpen(true)}
            onCloseResults={() => setResultsOpen(false)}
            inputId="home-search-input"
          />
          <SearchResults
            query={search.query}
            scope={search.scope}
            hits={search.hits}
            totalCount={search.totalCount}
            loading={search.loading}
            error={search.error}
            hasMore={search.hasMore}
            open={resultsOpen && search.query.trim().length > 0}
            showWorkspaceColumn
            onPivotToAll={() => search.setScope("all")}
            onPickHit={(hit) => {
              setOpenThreadParent(hit.parent_ts ?? null);
              setResultsOpen(false);
              navigate(
                `/browse/${hit.team_id}/${hit.conversation_id}` +
                  `?ts=${encodeURIComponent(hit.ts)}`,
              );
            }}
            onLoadMore={search.loadMore}
            onClose={() => setResultsOpen(false)}
          />
        </div>

        <div className="flex items-center gap-3 flex-shrink-0 ml-auto md:ml-0">
          {isSuperadmin && <SystemChip health={health} onClick={() => setSystemOpen(true)} />}
          <Avatar name={username} size={34} />
          <SignOutButton />
        </div>
      </header>

      {/* ── main ──────────────────────────────────────────────── */}
      <main
        className={`flex-1 w-full mx-auto px-6 sm:px-10 pb-20 flex flex-col gap-8 ${
          useConsole ? "max-w-5xl" : "max-w-3xl"
        }`}
      >
        {/* greeting + the one aggregate line */}
        <div className="pt-3">
          <h1 className="text-[clamp(28px,4vw,36px)] font-bold tracking-tightish leading-tight">
            {greetingWord(new Date().getHours())}, {username}
          </h1>
          {summary && (
            <p data-testid="chooser-summary" className="text-body-sm text-theme-text-secondary mt-2.5">
              {summary.workspaces} workspace{summary.workspaces === 1 ? "" : "s"}
              {useConsole && attentionCount > 0 && (
                <>
                  {" · "}
                  <span className="text-theme-warning-text">{attentionCount} need attention</span>
                </>
              )}
              {" · "}
              {summary.messages.toLocaleString()} messages
              {!useConsole && (
                <>
                  {" · "}
                  {summary.attachments.toLocaleString()} attachments
                </>
              )}
            </p>
          )}
        </div>

        {useConsole ? (
          /* superadmin: dense, searchable console that scales to hundreds */
          <WorkspaceConsole
            workspaces={workspaces}
            driveQueue={driveQueue}
            attentionCount={attentionCount}
            onAdd={() => setAddOpen(true)}
            onClaude={() => setClaudeOpen(true)}
          />
        ) : (
          /* regular user: friendly hero + a balanced utility shelf */
          <>
            {hero ? <Hero ws={hero} owner={null} /> : status && <EmptyState />}

            {others.length > 0 && (
              <section className="flex flex-col gap-4">
                <h2 className="text-h3 font-semibold">
                  Your workspaces
                  <span className="ml-2 text-theme-text-muted font-normal">{workspaces.length}</span>
                </h2>
                <div
                  data-testid="chooser-workspaces"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  {others.map((w) => (
                    <WorkspaceCard key={w.team_id} ws={w} driveQueue={driveQueue} owner={null} />
                  ))}
                  <AddTile onClick={() => setAddOpen(true)} isSuperadmin={false} />
                </div>
              </section>
            )}

            <section
              className={`grid grid-cols-1 gap-4 ${
                others.length === 0 ? "sm:grid-cols-3" : "sm:grid-cols-2"
              }`}
            >
              <AskClaudeCard onClick={() => setClaudeOpen(true)} />
              <MyWorkspacesCard />
              {others.length === 0 && (
                <AddShelfCard onClick={() => setAddOpen(true)} isSuperadmin={false} />
              )}
            </section>
          </>
        )}
      </main>

      {isSuperadmin && systemOpen && (
        <SystemDrawer status={status} health={health} onClose={() => setSystemOpen(false)} />
      )}
      {addOpen && <AddWorkspaceModal onClose={() => setAddOpen(false)} />}
      {claudeOpen && <ConnectClaudeModal onClose={() => setClaudeOpen(false)} />}
    </div>
  );
}

// ── workspace console (superadmin · scales to hundreds) ─────────────
//
// An admin's estate can be 100+ workspaces, so cards don't cut it — this
// is a dense, filterable, sortable list. A toolbar filters by free text
// and BY USER (owner), toggles "needs attention", and sorts; actions
// (Add / Manage / Ask Claude) sit in a compact action row.

type SortMode = "recent" | "name" | "active";
const SORT_LABEL: Record<SortMode, string> = {
  recent: "Recent activity",
  name: "Name",
  active: "Most messages",
};

// Custom dark dropdown — replaces native <select>, whose option popup the
// browser paints in the document's "light" color-scheme (white-on-white,
// unreadable on this dark UI). Fully styled, so it's consistent and
// browser-independent.
function Dropdown({
  value,
  options,
  onChange,
  ariaLabel,
  testid,
  align = "left",
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
  ariaLabel: string;
  testid?: string;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const current = options.find((o) => o.value === value)?.label ?? value;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        data-testid={testid}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-xl bg-white/[0.05] border border-white/[0.10] text-sm text-theme-text-primary hover:bg-white/[0.08] transition-colors"
      >
        <span className="truncate max-w-[140px]">{current}</span>
        <ChevronDown className="w-3.5 h-3.5 text-theme-text-muted flex-shrink-0" strokeWidth={2} aria-hidden />
      </button>
      {open && (
        <div
          role="listbox"
          className={`absolute z-30 mt-1.5 min-w-full w-max max-w-[240px] max-h-64 overflow-y-auto rounded-xl ${GLASS} shadow-[0_14px_36px_rgba(0,0,0,0.45)] py-1 ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-white/[0.08] transition-colors ${
                o.value === value ? "text-theme-text-primary" : "text-theme-text-secondary"
              }`}
            >
              <span className="truncate">{o.label}</span>
              {o.value === value && (
                <Check className="w-3.5 h-3.5 ml-auto flex-shrink-0 text-theme-accent-link" strokeWidth={2.4} aria-hidden />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WorkspaceConsole({
  workspaces,
  driveQueue,
  attentionCount,
  onAdd,
  onClaude,
}: {
  workspaces: SystemStatusWorkspace[];
  driveQueue: Parameters<typeof workspaceHealthColor>[0]["driveQueue"];
  attentionCount: number;
  onAdd: () => void;
  onClaude: () => void;
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>("recent");
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState("all");

  // Distinct owners (users) for the "by user" filter — null vault owners
  // collapse to "unattributed", matching how the rows label them.
  const owners = useMemo(() => {
    const set = new Set<string>();
    for (const w of workspaces) set.add(w.owned_by_username ?? "unattributed");
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [workspaces]);

  // The admin always gets the filter bar — at estate scale it's essential
  // and the chrome should be steady. (Hidden only when there's nothing.)
  const showToolbar = workspaces.length > 0;

  const rows = useMemo(() => {
    const recency = (w: SystemStatusWorkspace) =>
      w.last_message_at ? Date.parse(w.last_message_at) : 0;
    let list = workspaces.map((w) => ({
      w,
      color: workspaceHealthColor({ workspace: w, driveQueue }),
      label: workspaceHealthLabel({ workspace: w, driveQueue }),
    }));
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        ({ w }) =>
          (w.team_name || "").toLowerCase().includes(q) ||
          w.team_id.toLowerCase().includes(q) ||
          (w.owned_by_username || "").toLowerCase().includes(q),
      );
    }
    if (ownerFilter !== "all")
      list = list.filter(({ w }) => (w.owned_by_username ?? "unattributed") === ownerFilter);
    if (onlyAttention) list = list.filter(({ color }) => color === "red" || color === "yellow");
    list.sort((a, b) => {
      if (sort === "name")
        return (a.w.team_name || a.w.team_id).localeCompare(b.w.team_name || b.w.team_id);
      if (sort === "active") return b.w.message_count - a.w.message_count;
      return recency(b.w) - recency(a.w) || a.w.team_id.localeCompare(b.w.team_id);
    });
    return list;
  }, [workspaces, driveQueue, query, sort, onlyAttention, ownerFilter]);

  // [color-scheme:dark] — the app's <meta color-scheme> is "light", which
  // makes a native <select> paint its option popup WHITE; with our near-
  // white option text that renders white-on-white (unreadable). Forcing
  // dark scheme on these controls makes the native popup dark + legible.
  const inputCls =
    "h-9 rounded-xl bg-white/[0.05] border border-white/[0.10] text-sm text-theme-text-primary placeholder:text-theme-text-muted focus:outline-none focus:border-white/[0.2] [color-scheme:dark]";
  const btnCls =
    "inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-white/[0.06] border border-white/[0.12] text-xs font-semibold text-theme-text-secondary hover:bg-white/[0.09] transition-colors whitespace-nowrap";

  return (
    <section className="flex flex-col gap-4">
      {/* heading + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-h3 font-semibold">
          All workspaces
          <span className="ml-2 text-theme-text-muted font-normal">{workspaces.length}</span>
        </h2>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClaude} data-testid="chooser-connect-claude" className={btnCls}>
            <span className="text-sm font-bold leading-none">✦</span>
            <span className="hidden sm:inline">Ask Claude</span>
          </button>
          <Link to="/users" data-testid="chooser-users" aria-label="Manage workspaces and people" className={btnCls}>
            <Users className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
            <span className="hidden sm:inline">Manage people</span>
          </Link>
          <button type="button" onClick={onAdd} data-testid="chooser-add-workspace" aria-label="Add a workspace" className={btnCls}>
            <Plus className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
            <span className="hidden sm:inline">Add workspace</span>
          </button>
        </div>
      </div>

      {/* filter + sort — only when the list is long enough to need it */}
      {showToolbar && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-text-muted" strokeWidth={2} aria-hidden />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name, ID or owner…"
              aria-label="Filter workspaces"
              data-testid="chooser-workspace-filter"
              className={`${inputCls} w-full pl-9 pr-3`}
            />
          </div>
          {/* by user (owner) */}
          <Dropdown
            ariaLabel="Filter by user"
            testid="chooser-workspace-owner-filter"
            value={ownerFilter}
            onChange={setOwnerFilter}
            options={[{ value: "all", label: "All users" }, ...owners.map((o) => ({ value: o, label: o }))]}
          />
          {attentionCount > 0 && (
            <button
              type="button"
              onClick={() => setOnlyAttention((v) => !v)}
              aria-pressed={onlyAttention}
              className={`${btnCls} ${onlyAttention ? "!border-theme-warning-text/50 !text-theme-warning-text" : ""}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-theme-warning-text" />
              {attentionCount} need attention
            </button>
          )}
          <Dropdown
            ariaLabel="Sort workspaces"
            value={sort}
            onChange={(v) => setSort(v as SortMode)}
            align="right"
            options={(Object.keys(SORT_LABEL) as SortMode[]).map((k) => ({ value: k, label: SORT_LABEL[k] }))}
          />
        </div>
      )}

      {/* the list */}
      <div className={`rounded-2xl ${GLASS} overflow-hidden`} data-testid="chooser-workspaces">
        {rows.length === 0 ? (
          <p className="px-4 py-10 text-center text-body-sm text-theme-text-muted">
            {workspaces.length === 0
              ? "No workspaces yet — add one to begin."
              : `No workspaces match “${query}”.`}
          </p>
        ) : (
          <div className="max-h-[62vh] overflow-y-auto divide-y divide-white/[0.06]">
            {rows.map(({ w, color, label }) => (
              <WorkspaceRow key={w.team_id} ws={w} color={color} label={label} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function WorkspaceRow({
  ws,
  color,
  label,
}: {
  ws: SystemStatusWorkspace;
  color: HealthColor;
  label: string;
}) {
  const name = ws.team_name || ws.team_id;
  const owner = ws.owned_by_username ?? "unattributed";
  return (
    <Link
      to={`/browse/${encodeURIComponent(ws.team_id)}`}
      data-testid={`chooser-workspace-${ws.team_id}`}
      aria-label={`Browse ${name}`}
      className="flex items-center gap-4 px-4 py-2.5 hover:bg-white/[0.045] transition-colors group focus-visible:outline-none focus-visible:bg-white/[0.05]"
    >
      <Avatar name={name} size={34} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{name}</div>
        <div className="text-[11px] text-theme-text-muted truncate">
          <span className="font-mono">{ws.team_id}</span>
          <span data-testid={`chooser-workspace-owner-${ws.team_id}`}> · {owner}</span>
        </div>
      </div>
      <span className="hidden sm:flex items-center gap-1.5 w-32 flex-shrink-0">
        <span className={`w-1.5 h-1.5 rounded-full ${HEALTH_DOT[color]}`} />
        <span className={`text-[11px] font-medium whitespace-nowrap ${HEALTH_TEXT[color]}`}>{label}</span>
      </span>
      <span className="hidden lg:block text-[11px] text-theme-text-muted w-28 text-right tabular-nums flex-shrink-0">
        {ws.message_count.toLocaleString()} · {ws.attachment_count.toLocaleString()}
      </span>
      <span className="text-[11px] text-theme-text-muted w-16 text-right whitespace-nowrap flex-shrink-0">
        {ws.last_message_at ? formatRelative(ws.last_message_at) : "—"}
      </span>
      <ArrowRight
        className="w-4 h-4 text-theme-text-muted/50 group-hover:text-theme-text-secondary transition-colors flex-shrink-0"
        strokeWidth={2}
        aria-hidden
      />
    </Link>
  );
}

// ── hero ────────────────────────────────────────────────────────────
//
// The single most-recently-active workspace. Owner + recency answer "is
// this the right place?"; the one gradient button is the only loud thing
// on the page. No message/file count line — those live once, in the
// summary above (and per-card in the grid).

function Hero({ ws, owner = null }: { ws: SystemStatusWorkspace; owner?: string | null }) {
  const name = ws.team_name || ws.team_id;
  const color = workspaceHealthColor({ workspace: ws, driveQueue: null });
  const label = workspaceHealthLabel({ workspace: ws, driveQueue: null });
  return (
    <div className={`rounded-3xl ${GLASS} p-6 sm:p-7 shadow-[0_18px_50px_rgba(0,0,0,0.34)]`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6">
        <Avatar name={name} size={56} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5">
            <h2 className="text-h2 font-bold truncate">{name}</h2>
            <span className="inline-flex items-center gap-1.5 flex-shrink-0">
              <span className={`w-1.5 h-1.5 rounded-full ${HEALTH_DOT[color]}`} />
              <span className={`text-[11px] font-medium whitespace-nowrap ${HEALTH_TEXT[color]}`}>{label}</span>
            </span>
          </div>
          <p className="text-body-sm text-theme-text-secondary mt-1.5 truncate">
            <span className="font-mono text-theme-text-muted">{ws.team_id}</span>
            {owner && <> · owned by {owner}</>}
            {ws.last_message_at && <> · last activity {formatRelative(ws.last_message_at)}</>}
          </p>
        </div>
        <Link
          to={`/browse/${encodeURIComponent(ws.team_id)}`}
          data-testid="chooser-browse"
          aria-label={`Browse ${name}`}
          style={{ background: ACCENT }}
          className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl text-white text-sm font-semibold shadow-[0_10px_26px_rgba(124,92,255,0.42)] hover:opacity-95 transition-opacity"
        >
          Browse this archive
          <ArrowRight className="w-4 h-4" strokeWidth={2.2} aria-hidden />
        </Link>
      </div>
    </div>
  );
}

// ── empty state (no workspaces) ─────────────────────────────────────

function EmptyState() {
  return (
    <div className={`rounded-3xl ${GLASS} p-10 text-center shadow-[0_14px_40px_rgba(0,0,0,0.28)]`}>
      <div
        aria-hidden
        style={{ background: ACCENT }}
        className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center text-white shadow-[0_8px_20px_rgba(124,92,255,0.4)]"
      >
        <Plus className="w-6 h-6" strokeWidth={2} />
      </div>
      <h2 className="text-h3 font-semibold mt-5">No workspaces yet</h2>
      <p className="text-body-sm text-theme-text-secondary mt-1.5">
        Add your Slack below — it’s archived automatically.
      </p>
    </div>
  );
}

// ── workspace card (grid, N ≥ 2 only) ───────────────────────────────

function WorkspaceCard({
  ws,
  driveQueue,
  owner = null,
}: {
  ws: SystemStatusWorkspace;
  driveQueue: Parameters<typeof workspaceHealthColor>[0]["driveQueue"];
  /** Superadmin view: the owning user's name ("unattributed" when the
   *  vault row has no owner). null for regular users — superadmin
   *  never owns workspaces, so their Home must say WHOSE each one is. */
  owner?: string | null;
}) {
  const color = workspaceHealthColor({ workspace: ws, driveQueue });
  const label = workspaceHealthLabel({ workspace: ws, driveQueue });
  const reason = workspaceHealthReason({ workspace: ws, driveQueue });
  const name = ws.team_name || ws.team_id;
  return (
    <Link
      to={`/browse/${encodeURIComponent(ws.team_id)}`}
      data-testid={`chooser-workspace-${ws.team_id}`}
      aria-label={`Browse ${name}`}
      title={reason}
      className={`group rounded-2xl ${GLASS_QUIET} p-5 flex flex-col gap-3.5 hover:bg-white/[0.06] hover:border-white/[0.15] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus-ring`}
    >
      <div className="flex items-center justify-between">
        <Avatar name={name} size={42} />
        <span className="inline-flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${HEALTH_DOT[color]}`} />
          <span className={`text-[11px] font-medium whitespace-nowrap ${HEALTH_TEXT[color]}`}>{label}</span>
        </span>
      </div>
      <div>
        <div className="text-[15px] font-semibold truncate">{name}</div>
        <div className="text-[11px] text-theme-text-muted truncate mt-0.5">
          <span className="font-mono">{ws.team_id}</span>
          {owner && (
            <span data-testid={`chooser-workspace-owner-${ws.team_id}`}>
              {" · owned by "}
              <span className="text-theme-text-secondary">{owner}</span>
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 text-[11px] text-theme-text-muted mt-auto pt-1">
        <span className="whitespace-nowrap">
          <span className="text-theme-text-secondary font-semibold">{ws.message_count.toLocaleString()}</span> messages
        </span>
        <span className="whitespace-nowrap">
          <span className="text-theme-text-secondary font-semibold">{ws.attachment_count.toLocaleString()}</span> files
        </span>
        {ws.last_message_at && (
          <span className="ml-auto whitespace-nowrap">{formatRelative(ws.last_message_at)}</span>
        )}
      </div>
    </Link>
  );
}

// ── add-workspace (dashed grid tile — trails the workspaces grid) ────

const ADD_BLURB = {
  superadmin: "Sign in to any Slack — attributed to its owner by email.",
  user: "Sign in with your own Slack — archived automatically.",
};

function AddTile({ onClick, isSuperadmin = false }: { onClick: () => void; isSuperadmin?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="chooser-add-workspace"
      aria-label="Add a workspace"
      className="group rounded-2xl border border-dashed border-white/[0.14] hover:border-white/[0.24] hover:bg-white/[0.03] transition-colors text-left flex flex-col items-start justify-center gap-2 p-5 min-h-[140px]"
    >
      <span className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.10] flex items-center justify-center flex-shrink-0 group-hover:bg-white/[0.10]">
        <Plus className="w-5 h-5 text-theme-text-secondary" strokeWidth={2} aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">Add a workspace</span>
        <span className="block text-[11px] text-theme-text-muted mt-0.5 leading-relaxed">
          {isSuperadmin ? ADD_BLURB.superadmin : ADD_BLURB.user}
        </span>
      </span>
    </button>
  );
}

// ── utility-shelf cards ─────────────────────────────────────────────

function ShelfCard({
  icon,
  title,
  blurb,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  blurb: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`rounded-2xl ${GLASS_QUIET} p-5 flex flex-col`}>
      {/* Icon on top so the title gets the full card width (no wrap),
          and the loud gradient stays reserved for the hero CTA. */}
      <span
        aria-hidden
        className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/[0.06] border border-white/[0.10] text-theme-text-secondary"
      >
        {icon}
      </span>
      <div className="mt-3.5 flex-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-[11px] text-theme-text-muted mt-1 leading-relaxed">{blurb}</div>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

// Every user can connect Claude to their OWN slice of the archive
// (read-only token, RBAC-scoped). Both roles see this.
function AskClaudeCard({ onClick }: { onClick: () => void }) {
  return (
    <ShelfCard
      icon={<span className="text-lg font-bold">✦</span>}
      title="Ask Claude"
      blurb="Read-only access to your archive"
    >
      <button
        type="button"
        onClick={onClick}
        data-testid="chooser-connect-claude"
        className="flex w-full items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.12] text-xs font-semibold text-theme-text-secondary hover:bg-white/[0.09] transition-colors"
      >
        Generate access token
      </button>
    </ShelfCard>
  );
}

// Regular users don't get system internals — just a door to their own
// /status (which shows only their workspaces, no system cards).
function MyWorkspacesCard() {
  return (
    <ShelfCard
      icon={<Activity className="w-5 h-5" strokeWidth={2} />}
      title="My workspaces"
      blurb="Health, sync & Drive state"
    >
      <Link
        to="/status"
        data-testid="chooser-status"
        aria-label="Open my workspaces status"
        className="flex w-full items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.12] text-xs font-semibold text-theme-text-secondary hover:bg-white/[0.09] transition-colors"
      >
        Open status
        <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </Link>
    </ShelfCard>
  );
}

// Add-workspace as a shelf card (used at N<=1, where there's no grid to
// trail) — same footprint as the other doors so the row stays even.
function AddShelfCard({ onClick, isSuperadmin }: { onClick: () => void; isSuperadmin: boolean }) {
  return (
    <ShelfCard
      icon={<Plus className="w-5 h-5" strokeWidth={2} />}
      title="Add a workspace"
      blurb={isSuperadmin ? "Attributed to its owner by email" : "Archived automatically"}
    >
      <button
        type="button"
        onClick={onClick}
        data-testid="chooser-add-workspace"
        aria-label="Add a workspace"
        className="flex w-full items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.12] text-xs font-semibold text-theme-text-secondary hover:bg-white/[0.09] transition-colors"
      >
        Connect
        <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </button>
    </ShelfCard>
  );
}

// ── System chip + drawer (superadmin · the only home for ops) ───────

function SystemChip({ health, onClick }: { health: Health; onClick: () => void }) {
  return (
    <button
      type="button"
      data-testid="chooser-health-pulse"
      onClick={onClick}
      aria-label="Open system status"
      className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.10] hover:bg-white/[0.09] transition-colors"
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${HEALTH_DOT[health.color]} ${
          health.color === "green" ? "shadow-[0_0_6px_var(--theme-success-text)]" : ""
        }`}
      />
      <span className={`text-[11px] font-medium tracking-wide ${HEALTH_TEXT[health.color]}`}>
        {health.label}
      </span>
      <ChevronDown className="w-3 h-3 text-theme-text-muted" strokeWidth={2} aria-hidden />
    </button>
  );
}

function SystemDrawer({
  status,
  health,
  onClose,
}: {
  status: SystemStatusResponse | null;
  health: Health;
  onClose: () => void;
}) {
  const driveEnabled = status?.drive_enabled ?? false;
  const dead = status?.drive_queue?.dead ?? 0;
  const cacheBytes = status?.cache_bytes ?? null;
  const cacheCap = status?.cache_cap_bytes ?? null;
  const over = cacheBytes != null && cacheCap != null && cacheCap > 0 && cacheBytes > cacheCap;
  const pct = cacheBytes != null && cacheCap ? Math.min(100, Math.round((cacheBytes / cacheCap) * 100)) : null;
  const mb = (b: number) => `${Math.round(b / (1024 * 1024))} MB`;

  const panelRef = useRef<HTMLElement | null>(null);

  // Modal a11y: Esc closes; focus moves into the drawer on open, is
  // trapped within it while open, and is restored to the opener (the
  // System chip) on close; body scroll is locked.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const focusables = () =>
      panel
        ? Array.from(
            panel.querySelectorAll<HTMLElement>(
              'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          )
        : [];
    focusables()[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const f = focusables();
        if (f.length === 0) return;
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      opener?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="System status">
      <button
        type="button"
        aria-label="Close system status"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
      />
      <aside
        ref={panelRef}
        className={`relative h-full w-full max-w-[380px] ${GLASS} border-l border-white/[0.12] shadow-[-20px_0_60px_rgba(0,0,0,0.45)] p-6 overflow-y-auto`}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-h3 font-semibold">System status</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close system status"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-theme-text-muted hover:bg-white/[0.07] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={2} aria-hidden />
          </button>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <span className={`w-2 h-2 rounded-full ${HEALTH_DOT[health.color]}`} />
          <span className={`text-sm font-semibold ${HEALTH_TEXT[health.color]}`}>{health.label}</span>
        </div>

        <div className="flex flex-col gap-1 mb-6">
          <Row k="Google Drive" v={driveEnabled ? "Enabled" : "Disabled"} vc={driveEnabled ? "text-theme-success-text" : "text-theme-text-secondary"} />
          <Row k="Upload queue" v={`${dead} dead`} vc={dead > 0 ? "text-theme-warning-text" : undefined} />
        </div>

        {cacheBytes != null && cacheCap != null && pct != null && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-theme-text-secondary">Local cache</span>
              <span className="font-mono text-[11px] text-theme-text-secondary">
                {mb(cacheBytes)} / {mb(cacheCap)}
                {over && <span className="ml-1.5 text-theme-warning-text">⚠ over</span>}
              </span>
            </div>
            <div className="h-2 rounded-full bg-white/[0.08] overflow-hidden">
              <div
                style={{
                  width: `${pct}%`,
                  background: over
                    ? "linear-gradient(90deg, #f59e0b, #f43f5e)"
                    : "linear-gradient(90deg, #8b6cff, #22d3ee)",
                }}
                className="h-full rounded-full"
              />
            </div>
          </div>
        )}

        <Link
          to="/status"
          data-testid="chooser-status"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-theme-accent-link hover:opacity-80 transition-opacity"
        >
          <Activity className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
          Open full system status
          <ArrowRight className="w-3 h-3" strokeWidth={2} aria-hidden />
        </Link>
      </aside>
    </div>
  );
}

function Row({ k, v, vc }: { k: string; v: string; vc?: string }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-theme-text-secondary">{k}</span>
      <span className={`text-sm font-semibold ${vc ?? "text-theme-text-primary"}`}>{v}</span>
    </div>
  );
}
